import React, { ReactNode } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

interface SummaryCardProps {
  title: ReactNode;
  value: number;
  subtitle?: string;
  dateRange?: string;
  tentativeValue?: number;
  fill?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  subtitle,
  dateRange,
  tentativeValue,
  fill = false,
}) => {
  const formatting = useFormatting();
  const getTitleText = (): string => {
    if (typeof title === 'string') return title;
    return '';
  };

  const getUnderlineColor = () => {
    const label = getTitleText();
    if (label === 'Total Income' || label === 'Income') return 'green';
    if (label === 'Total Expenses' || label === 'Expense') return 'red';
    return 'black';
  };

  const getBalanceColor = () => {
    if (value < 0) return 'red';
    if (value > 0) return 'green';
    return 'black';
  };

  const underlineStyle = {
    textDecoration: 'underline',
    textDecorationColor: getUnderlineColor(),
    textDecorationThickness: '1.5px',
    textUnderlineOffset: '4px',
  };

  return (
    <Card
      sx={{
        ...(fill
          ? {
              width: '100%',
              maxWidth: 'none',
            }
          : {
              flex: '1 1 200px',
              maxWidth: 220,
              minWidth: 160,
              flexGrow: 1,
            }),
      }}
    >

      <CardContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          '&:last-child': {
            pb: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            ...underlineStyle,
            fontWeight: 'bold',
            mb: { xs: 1, sm: 1.5 },
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="h5"
          sx={{
            color:
              getTitleText() === 'Cash Balance (Checking)' || getTitleText() === 'Cash Balance'
                ? getBalanceColor()
                : 'black',
            fontWeight: 'bold',
            fontSize: { xs: '1.3rem', sm: '1.5rem' },
            mb: { xs: 1, sm: 1.5 },
            lineHeight: 1.2,
          }}
        >
          {formatMoney(value, formatting)}
        </Typography>

        {subtitle && (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mb: 0.5 }}
          >
            {subtitle}
          </Typography>
        )}

        {dateRange && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block' }}
          >
            {dateRange}
          </Typography>
        )}

        {tentativeValue !== undefined && tentativeValue !== 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 500,
              mt: 0.5,
            }}
          >
            (+{formatMoney(tentativeValue, formatting)} tentative)
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SummaryCard;
